import TopbarHyperflowSupportChat from '@eduzz/ui-layout/Topbar/HyperflowSupportChat';
import { API_MAP } from 'helpers/api';
import React, { useState, useEffect } from 'react';
import { HYPERFLOW_CHAT_UNIT_ID, HYPERFLOW_CHAT_BLACK_ID, HYPERFLOW_CHAT_ELITE_ID, HYPERFLOW_FLOW_ID } from 'settings';
import useGetOriginalUser from 'v2/hooks/useGetOriginalUser';
import useGetUser from 'v2/hooks/useGetUser';
import { apiServiceProxy } from 'v2/services/api-proxy';
import eduzzAccount from 'v2/services/eduzzAccount';

import type { CurrentUser } from '@eduzz/ui-layout/Topbar/HyperflowSupportChat/types';

const HyperflowChat = () => {
  const user = useGetUser();
  const originalUser = useGetOriginalUser();
  const isProducer = user?.producer || user?.coproducer || user?.affiliate;
  const isAccessPolicy = user?.id !== originalUser?.id;

  const buildUserObject = (isAccessPolicy: boolean): CurrentUser => {
    const baseUser = {
      id: user?.id,
      name: user?.full_name,
      email: user?.email,
      belt: !user?.current_elite_level?.id ? 'White' : eduzzAccount.getBeltById(user?.current_elite_level?.id),
      tag: !user?.acceptence_terms ? null : user?.is_clube_black ? 'unity' : 'pro',
      isClubeBlack: user?.is_clube_black,
      isAccessPolicy,
    };

    if (isAccessPolicy) {
      return {
        ...baseUser,
        originalUserId: originalUser?.id,
        originalUserName: originalUser?.name,
        originalUserEmail: originalUser?.email,
      } as CurrentUser;
    }

    return baseUser as CurrentUser;
  };

  const canViewHyperflowChat = isProducer;
  const userToHyperflow: CurrentUser = buildUserObject(isAccessPolicy);

  const configToHyperflow = {
    chatUnityID: HYPERFLOW_CHAT_UNIT_ID,
    chatBlackID: HYPERFLOW_CHAT_BLACK_ID,
    chatEliteID: HYPERFLOW_CHAT_ELITE_ID,
    flowId: HYPERFLOW_FLOW_ID,
    origin: 'myeduzz',
  } as const;

  const createAccountsJwtPromise = () => {
    return new Promise<string>(async (resolve, reject) => {
      const ssid = localStorage.getItem('ssid');
      try {
        const jwtToHyperflow = await apiServiceProxy.post(`${API_MAP.getChatTokenBySsid}`, { ssid });
        const token = jwtToHyperflow?.data?.token;
        token ? resolve(token as string) : reject('Token not created or empty');
      } catch {
        reject('Error in token creation');
      }
    });
  };

  const [hyperflowJwt, setHyperflowJwt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const createHyperflowJwtPromise = async () => {
      return new Promise<string>(async (resolve, reject) => {
        try {
          const hyperflowJwt = await apiServiceProxy.post(`${API_MAP.getHyperflowToken}`, { userToHyperflow });
          const hyperflowChatToken = hyperflowJwt?.data?.hyperflowToken;
          if (hyperflowChatToken) {
            resolve(hyperflowChatToken as string);
          } else {
            reject('JWT not created or empty');
          }
        } catch {
          reject('Error in hyperflowChatToken creation');
        }
      });
    };

    if (userToHyperflow.id) {
      const fetchJwt = async () => {
        try {
          const token = await createHyperflowJwtPromise();
          setHyperflowJwt(token);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      };

      fetchJwt();
    }

    return () => {
      const alreadyHasHyperflowInDOM = !!document.getElementById('hyper_iframe');
      const searchParams = new URLSearchParams(window.location.search);
      const urlAction = searchParams.get('hf-action');
      const hasActionURL = !!urlAction;

      const triggerURLAction = () => {
        if (hasActionURL) {
          if (urlAction === 'open') Hyperflow.open();

          searchParams.delete('hf-action');
          const newSearchParams = searchParams.toString();
          const newUrl = `${window.location.pathname}${!!newSearchParams ? `?${newSearchParams}` : ''}`;

          window.history.replaceState({}, '', newUrl);
        }
      };

      if (typeof Hyperflow !== 'undefined' && alreadyHasHyperflowInDOM) {
        Hyperflow.clear();
        triggerURLAction();
      }
    };
  }, [userToHyperflow]);

  return (
    <>
      {canViewHyperflowChat && !loading && hyperflowJwt && (
        <TopbarHyperflowSupportChat
          accountsJwt={createAccountsJwtPromise}
          currentUser={userToHyperflow}
          hyperflowConfig={configToHyperflow}
          hyperflowJwt={hyperflowJwt}
        />
      )}
    </>
  );
};
export default HyperflowChat;
